import { useMemo, useRef, useState } from 'react';
import { ChevronDown, Home, Link2, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import type { FlowView, SavedViewWire } from '@shared/flow/view';
import { ResponsivePopover } from '../ResponsivePopover';
import { BrowseAllModal } from './BrowseAllModal';
import { useViewNameDialog } from './ViewNameDialog';
import { ownerDisplayLabel, type FlowViewMachine } from './useViewStateMachine';
import { AUTOFILL_IGNORE_PROPS } from '../../../lib/autofill';

/**
 * ViewSwitcher — the two-tier view dropdown, per the prototype's behavioral
 * contract (`docs/flow/custom-views-prototype.html`, `renderSwitcher()`
 * ~line 510): a pinned search box, then Presets → Your views → Team views,
 * then `＋ New view` and `⌕ Browse all views…`, with a copy-link affordance
 * on every row.
 *
 * Added for the build (not in the prototype): rename and delete, offered
 * ONLY on views the acting user created. Presets are never editable,
 * renamable, or deletable — they change by deploy — and another rep's view
 * isn't yours to change either (the server enforces both with a 403; this is
 * the matching affordance rule).
 *
 * A row is a `<div>` holding several sibling buttons rather than one button
 * with nested buttons (invalid HTML, and the nested controls would be
 * unreachable): the leading button opens the view, the trailing ones act on
 * it. Every trailing button carries an explicit `aria-label` naming its view
 * so the accessibility tree — what screen readers and `ab snapshot` read —
 * distinguishes "Rename Q3 hit list" from "Rename Warm inbound".
 */

export interface ViewSwitcherProps {
  machine: FlowViewMachine;
  /**
   * Overrides the "Browse all views…" click. Optional — when omitted (every
   * real mount today), the switcher opens its OWN `BrowseAllModal` bound to
   * this same `machine`, which is the actual U6 behavior. The override exists
   * so a future caller that already owns a modal/host of its own (or a test)
   * can still observe the click without the switcher fighting it for control.
   */
  onBrowseAll?: () => void;
}

const ROW_BASE =
  'flex-1 truncate rounded px-2 py-1 text-left text-[12px] text-content-primary hover:bg-bg-secondary dark:text-nightshade-100 dark:hover:bg-nightshade-700';
const ICON_BUTTON =
  'rounded p-1 text-content-tertiary opacity-0 transition-opacity hover:bg-bg-secondary hover:text-content-primary focus:opacity-100 group-hover:opacity-100 dark:text-nightshade-500 dark:hover:bg-nightshade-700 dark:hover:text-nightshade-100';
const SECTION_LABEL =
  'px-2 pb-0.5 pt-2 text-[10.5px] font-semibold uppercase tracking-wide text-content-tertiary dark:text-nightshade-500';

interface SwitcherRow {
  id: string;
  name: string;
  /** "Global" | "You" | teammate's `ownerDisplayLabel` — same vocabulary as
   *  the trigger's `ownershipLabel` (U2b, decision c), the prototype's `by`
   *  chip. */
  byLabel: string;
  /**
   * The row's rename/delete target — non-null ONLY on views the acting user
   * created. A preset has no row to edit, and another rep's view would 403
   * on the server, so neither gets the affordance.
   */
  ownedView: SavedViewWire | null;
}

function presetRow(view: FlowView): SwitcherRow {
  return { id: view.id, name: view.name, byLabel: 'Global', ownedView: null };
}

function savedRow(view: SavedViewWire, mine: boolean): SwitcherRow {
  return {
    id: view.id,
    name: view.name,
    byLabel: mine ? 'You' : ownerDisplayLabel(view.creatorName),
    ownedView: mine ? view : null,
  };
}

function matches(row: SwitcherRow, query: string): boolean {
  if (!query) return true;
  return row.name.toLowerCase().includes(query) || row.byLabel.toLowerCase().includes(query);
}

export function ViewSwitcher({ machine, onBrowseAll }: ViewSwitcherProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [browseOpen, setBrowseOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  // One dialog for every name this surface asks for — "+ New view" and the
  // save-as-new `copyLink` pushes through on a modified view (U2a). Rename
  // is inline (U2b) and does not use this dialog.
  const { requestName, dialog: nameDialog } = useViewNameDialog(triggerRef);

  // Inline rename (U2b) — the row's leading button swaps for a text input.
  // `renamingId` names which row is being edited; `renameGuardRef` collapses
  // the double commit that Enter produces on its own (committing, which
  // unmounts the input, fires a synthetic blur — the SAME commit) into one.
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const renameGuardRef = useRef(false);

  const q = query.trim().toLowerCase();
  const presets = useMemo(() => machine.presets.map(presetRow).filter(r => matches(r, q)), [machine.presets, q]);
  const mine = useMemo(() => machine.myViews.map(v => savedRow(v, true)).filter(r => matches(r, q)), [machine.myViews, q]);
  const team = useMemo(() => machine.teamViews.map(v => savedRow(v, false)).filter(r => matches(r, q)), [machine.teamViews, q]);
  const nothingMatches = presets.length === 0 && mine.length === 0 && team.length === 0;

  // Closing the popover also ABANDONS any in-flight rename. Removing a focused
  // input from the DOM does not fire `blur`, so without this an outside-click
  // mid-rename would leave `renamingId` pointing at that row: reopen the
  // switcher and it is still stuck in edit mode, holding the stale draft.
  const close = () => {
    setOpen(false);
    setQuery('');
    renameGuardRef.current = true;
    setRenamingId(null);
  };
  const isDefaultView = (id: string) => machine.defaultViewId === id;

  /**
   * "+ New view" — name-first (U2a, decisions a+b). The row is created from
   * the CURRENT working state, so the rep keeps the filters/sort/columns they
   * were already looking at (including the role defaults the mount seeded);
   * `saveAsNew` lands on the create RESPONSE, never on an id round-trip (F6).
   */
  const handleNewView = async () => {
    close();
    const name = await requestName({ title: 'Name this view', confirmLabel: 'Create view' });
    if (name === null) return;
    await machine.saveAsNew(name);
  };

  /**
   * Opening a row — the switcher popover closes first, exactly like
   * `handleNewView` above, then the machine switches immediately (no
   * confirmation gate — see `useViewStateMachine.openView`'s doc for why).
   */
  const handleOpenView = async (id: string) => {
    close();
    await machine.openView(id);
  };

  const startRename = (view: SavedViewWire) => {
    renameGuardRef.current = false;
    setRenameValue(view.name);
    setRenamingId(view.id);
  };

  /**
   * Settles the rename session at most once (`renameGuardRef`) regardless of
   * how many of {Enter, blur, Escape} fire for it — committing or cancelling
   * unmounts the input, and an unmount blurs it, so the FIRST caller through
   * this function each session is the only one that gets to act.
   */
  const settleRename = (view: SavedViewWire, commit: boolean) => {
    if (renameGuardRef.current) return;
    renameGuardRef.current = true;
    setRenamingId(null);
    if (!commit) return;
    void machine.renameView(view, renameValue);
  };

  const renderRow = (row: SwitcherRow) => {
    const isRenaming = row.ownedView !== null && renamingId === row.id;
    return (
    <div
      key={row.id}
      data-testid="view-row"
      className={`group flex items-center gap-1 px-1 ${row.id === machine.activeViewId ? 'bg-bg-blue-light' : ''}`}
    >
      {isRenaming ? (
        <input
          {...AUTOFILL_IGNORE_PROPS}
          autoFocus
          value={renameValue}
          aria-label={`Rename ${row.name}`}
          onChange={e => setRenameValue(e.target.value)}
          onFocus={e => e.currentTarget.select()}
          onBlur={() => settleRename(row.ownedView as SavedViewWire, true)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              settleRename(row.ownedView as SavedViewWire, true);
            } else if (e.key === 'Escape') {
              // Cancel the RENAME, not the whole switcher — without this the
              // key event bubbles to ResponsivePopover's document-level
              // Escape listener and closes the popover out from under it.
              e.preventDefault();
              e.stopPropagation();
              settleRename(row.ownedView as SavedViewWire, false);
            }
          }}
          className="flex-1 rounded border border-content-action bg-white px-2 py-1 text-[12px] text-content-primary outline-none placeholder:text-content-placeholder dark:border-blueberry-500 dark:bg-nightshade-800 dark:text-nightshade-100"
        />
      ) : (
        <button
          type="button"
          onClick={() => { void handleOpenView(row.id); }}
          className={ROW_BASE}
        >
          {row.name}
          <span className="ml-1.5 text-[10.5px] text-content-tertiary dark:text-nightshade-500">{row.byLabel}</span>
        </button>
      )}
      {/* "Save as home view" — the per-user landing pointer. Works for a
          preset slug and a saved-view id alike; the house is filled on the
          view that currently holds the pointer, and that row's button
          renames itself rather than disappearing, so the state is readable
          from the accessibility tree instead of from a colour. Per entity:
          accounts and contacts each keep their own home, because this is
          backed by the per-(user, entity) working-state store. */}
      {machine.setDefaultView !== null && (
        isDefaultView(row.id) ? (
          <span
            title="Your home view"
            aria-label={`${row.name} is your home view`}
            className="rounded p-1 text-content-action dark:text-blueberry-400"
          >
            <Home className="h-3.5 w-3.5 fill-current" />
          </span>
        ) : (
          <button
            type="button"
            aria-label={`Save ${row.name} as your home view`}
            title="Save as home view"
            onClick={() => { machine.setDefaultView?.(row.id); }}
            className={ICON_BUTTON}
          >
            <Home className="h-3.5 w-3.5" />
          </button>
        )
      )}
      <button
        type="button"
        aria-label={`Copy link to ${row.name}`}
        title="Copy link"
        onClick={() => { close(); void machine.copyLink(row.id, suggested => requestName({ title: 'Name this view', initialValue: suggested, confirmLabel: 'Save view' })); }}
        className={ICON_BUTTON}
      >
        <Link2 className="h-3.5 w-3.5" />
      </button>
      {row.ownedView !== null && (
        <>
          <button
            type="button"
            aria-label={`Rename ${row.name}`}
            title="Rename"
            onClick={() => { startRename(row.ownedView as SavedViewWire); }}
            className={ICON_BUTTON}
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            aria-label={`Delete ${row.name}`}
            title="Delete"
            onClick={() => {
              const view = row.ownedView as SavedViewWire;
              // V1's only confirm guard — the machine itself never confirms
              // (thermo-nuclear review S2); V2 confirms via its own Glaze
              // `AlertDialog` (`DeleteViewConfirm`) instead of this dialog.
              if (!window.confirm(`Delete “${view.name}”? This removes it for everyone.`)) return;
              void machine.deleteView(view);
            }}
            className={ICON_BUTTON}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </>
      )}
    </div>
    );
  };

  return (
    <div className="relative inline-flex items-center gap-2">
      <button
        ref={triggerRef}
        type="button"
        aria-label="Change view"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => {
          setOpen(o => {
            const next = !o;
            // F11 — cross-user shared data, no invalidation channel; refetch
            // on open rather than trust the indefinite cache indefinitely.
            if (next) machine.refetchViews();
            return next;
          });
          setQuery('');
        }}
        title={`${machine.activeView.name} · ${machine.ownershipLabel}`}
        className="flex items-center gap-1.5 rounded-md border border-secondary bg-white px-2.5 py-1 text-[12px] font-medium text-content-primary hover:bg-bg-secondary dark:border-nightshade-700 dark:bg-nightshade-800 dark:text-nightshade-100 dark:hover:bg-nightshade-700"
      >
        {/* Trigger label — "ACCOUNT · <view name>"; ownership is not shown
            visibly (title carries it). */}
        <span className="rounded bg-bg-secondary px-1 text-[10px] uppercase text-content-tertiary dark:bg-nightshade-700 dark:text-nightshade-400">
          {machine.entity}
        </span>
        <span aria-hidden="true" className="text-content-tertiary dark:text-nightshade-600">·</span>
        <span className="max-w-[160px] truncate">{machine.activeView.name}</span>
        <ChevronDown className="h-3.5 w-3.5 text-content-tertiary dark:text-nightshade-500" />
      </button>

      <ResponsivePopover
        open={open}
        onClose={close}
        anchorRef={triggerRef}
        ariaLabel="Views"
        className="w-72 overflow-hidden"
      >
        <div className="relative border-b border-secondary dark:border-nightshade-700">
          <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-content-tertiary dark:text-nightshade-500" />
          <input
            {...AUTOFILL_IGNORE_PROPS}
            autoFocus
            value={query}
            onChange={e => setQuery(e.target.value)}
            aria-label="Search views"
            placeholder="Search views…"
            className="w-full bg-white py-1.5 pl-7 pr-2 text-[12px] text-content-primary outline-none placeholder:text-content-placeholder dark:bg-nightshade-800 dark:text-nightshade-100"
          />
        </div>

        <div className="max-h-72 overflow-auto py-1">
          {presets.length > 0 && (
            <>
              <div className={SECTION_LABEL}>Presets</div>
              {presets.map(renderRow)}
            </>
          )}

          <div className={SECTION_LABEL}>Your views</div>
          {mine.length > 0
            ? mine.map(renderRow)
            : (
              <div className="px-2 py-1 text-[11.5px] text-content-tertiary dark:text-nightshade-500">
                None yet — Save as new view
              </div>
            )}

          {team.length > 0 && (
            <>
              <div className={SECTION_LABEL}>Team views</div>
              {team.map(renderRow)}
            </>
          )}

          {nothingMatches && query.trim().length > 0 && (
            <div className="px-2 py-2 text-[11.5px] text-content-tertiary dark:text-nightshade-500">
              No views match “{query.trim()}”
            </div>
          )}
        </div>

        <div className="border-t border-secondary py-1 dark:border-nightshade-700">
          <button
            type="button"
            onClick={() => { void handleNewView(); }}
            className="flex w-full items-center gap-1.5 px-2 py-1 text-left text-[12px] text-content-primary hover:bg-bg-secondary dark:text-nightshade-100 dark:hover:bg-nightshade-700"
          >
            <Plus className="h-3.5 w-3.5" />
            New view
          </button>
          <button
            type="button"
            onClick={() => { close(); if (onBrowseAll) onBrowseAll(); else setBrowseOpen(true); }}
            className="flex w-full items-center gap-1.5 px-2 py-1 text-left text-[12px] text-content-primary hover:bg-bg-secondary dark:text-nightshade-100 dark:hover:bg-nightshade-700"
          >
            <Search className="h-3.5 w-3.5" />
            Browse all views…
          </button>
        </div>
      </ResponsivePopover>

      {/* Mounted only while shown — `BrowseAllModal` fetches the OTHER
          entity's saved views on mount, so a permanently-mounted instance
          would fire that request on every switcher render, not just when a
          rep actually opens it. */}
      {browseOpen && <BrowseAllModal open onClose={() => setBrowseOpen(false)} machine={machine} />}

      {nameDialog}
    </div>
  );
}
